import React, { useState } from 'react';
import { withRouter } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import { FocusDiv } from '@accedo/vdkweb-tv-ui';
import classNames from 'classnames';
import BaseModal from '../../common/BaseModal/BaseModal';
import { getModalData } from '#/redux/modules/modal/selectors';
import { actions, updateProfileId } from '#/redux/modules/onboarding/actions';
import { getDraftProfile } from '#/redux/modules/onboarding/selectors';
import { Avatar } from '#/widgets/ui';
import { getAvatars } from '#/redux/modules/firestore/selectors';
import { isUserLogged } from '#/redux/modules/user/selectors';
import styles from './ProfileToWatchNoneModal.scss';
import { addUnsavedProfiles } from '#/redux/modules/identity/actions';
import { actions as modalActions } from '#/redux/modules/modal/actions';
import FocusModal from '../../common/FocusModal/FocusModal';
// Focus handling
import {
  PROFILE_TO_WATCH_NONE_MODAL,
  VIZIO_KEYBOARD,
} from '#/utils/navigationHelper';
import routes from '#/utils/routes';
import { InfoInput } from '#/widgets/forms';
import { isWorkstation, isVIZIO } from '#/utils/componentStyleConfig';
import {
  getEditAvatar,
  getEditAvatarFocused,
} from '#/redux/modules/accedoOne/selectors';

const {
  CONTAINER,
  PROFILE_TO_WATCH_NONE_MODAL_ACTION_BUTTON,
  PROFILE_TO_WATCH_NONE_MODAL_CANCEL_BUTTON,
  PROFILE_TO_WATCH_NONE_MODAL_EDIT_AVATAR_BUTTON,
  PROFILE_TO_WATCH_NONE_MODAL_NAME_INPUT,
} = PROFILE_TO_WATCH_NONE_MODAL;

const navMap = {
  CONTAINER: {
    id: CONTAINER,
  },
  ACTION_BUTTON: {
    id: PROFILE_TO_WATCH_NONE_MODAL_ACTION_BUTTON,
  },
  CANCEL_BUTTON: {
    id: PROFILE_TO_WATCH_NONE_MODAL_CANCEL_BUTTON,
  },
  PROFILE_TO_WATCH_NONE_MODAL_EDIT_AVATAR_BUTTON: {
    id: PROFILE_TO_WATCH_NONE_MODAL_EDIT_AVATAR_BUTTON,
  },
  PROFILE_TO_WATCH_NONE_MODAL_NAME_INPUT: {
    id: PROFILE_TO_WATCH_NONE_MODAL_NAME_INPUT,
  },
  KEYBOARD: {
    id: VIZIO_KEYBOARD,
    nextup: PROFILE_TO_WATCH_NONE_MODAL_ACTION_BUTTON,
    parent: CONTAINER,
    useLastFocus: true,
  },
};

const ProfileToWatchNoneModal = ({ history }) => {
  const dispatch = useDispatch();
  const EditAvatar = useSelector(getEditAvatar);
  const EditAvatarFocused = useSelector(getEditAvatarFocused);
  const isUserLoggedIn = useSelector(isUserLogged);
  const profile = useSelector(getDraftProfile);
  const avatarOptions = useSelector(getAvatars);
  const { addProfile, age, name } = useSelector(state => getModalData(state));
  const rand = Math.floor(Math.random() * avatarOptions.length);
  const defaultAvatar = avatarOptions[rand];
  const [avatar, setAvatar] = useState(EditAvatar);
  const [isAvatarFocused, setIsAvatarFocused] = useState(false);

  const avatarDisplay = (
    <Avatar
      profile={{
        ...profile,
        avatar,
      }}
      className={classNames(styles.profileImage, {
        [styles.isFocused]: isAvatarFocused,
      })}
      focusEnabled
    />
  );

  const cancelAction = async () => {
    profile.avatar = defaultAvatar;
    const cloneProfile = {
      ...profile,
      age: `${age}`,
      birthdayMonth: '1',
      name,
    };
    dispatch({
      type: String(actions.setProfileAvatar),
      payload: defaultAvatar,
    });
    await dispatch(actions.setProfileFields(cloneProfile));

    await dispatch(actions.setProfileSkip(name));
    await dispatch(actions.saveProfile());

    if (isUserLoggedIn) {
      await dispatch(addUnsavedProfiles(updateProfileId));
    }
    history.push(routes.HOME);
    dispatch(modalActions.closeModal());
  };

  const avatarAction = async () => {
    dispatch(
      modalActions.openModal({
        modalId: isWorkstation() ? 'AvatarModal' : 'AvatarModalTV',
        successAction: {
          type: String(actions.setProfileAvatar),
        },
        modalData: {
          returnFocus: navMap.ACTION_BUTTON.id,
        },
      }),
    );
  };

  const saveAction = () => {
    dispatch(modalActions.closeModal());
    addProfile(profile.name, profile.avatar);
  };

  const handleFocus = () => {
    if (profile?.avatar) {
      setAvatar(profile.avatar);
      setIsAvatarFocused(true);
    } else {
      setAvatar(EditAvatarFocused);
    }
  };

  const handleBlur = () => {
    const showAvatar = profile?.avatar || EditAvatar;
    setAvatar(showAvatar);
    setIsAvatarFocused(false);
  };

  return (
    <FocusModal>
      <BaseModal
        saveButtonText="Create Profile"
        showCancelButton
        cancelButtonText="Skip"
        saveAction={() => () => saveAction()}
        cancelAction={cancelAction}
        saveButtonContainerClassName={styles.buttonContainer}
        saveButtonClassName={styles.createProfileButton}
        showCloseX={false}
        saveButtonDisabled={!(profile?.name && profile?.avatar)}
        cancelButtonClassName={styles.cancelButton}
        classNameFocused={styles.buttonFocused}
        nav={{
          parent: navMap.CONTAINER.id,
          id: navMap.ACTION_BUTTON.id,
          nextright: navMap.CANCEL_BUTTON.id,
          nextup: navMap.PROFILE_TO_WATCH_NONE_MODAL_NAME_INPUT.id,
          nextdown: isVIZIO() ? navMap.KEYBOARD.id : null,
        }}
        navCancel={{
          parent: navMap.CONTAINER.id,
          id: navMap.CANCEL_BUTTON.id,
          nextleft:
            profile?.name && profile?.avatar ? navMap.ACTION_BUTTON.id : null,
          nextup: navMap.PROFILE_TO_WATCH_NONE_MODAL_NAME_INPUT.id,
        }}
      >
        <FocusDiv
          className={styles.modalContainer}
          nav={{
            id: navMap.CONTAINER.id,
            forwardFocus: !(profile?.name && profile?.avatar)
              ? navMap.CANCEL_BUTTON.id
              : navMap.ACTION_BUTTON.id,
            useLastFocus: true,
          }}
        >
          <h2>Create Profile</h2>
          <p>
            Setup a profile and explore all your favorites and continue watching
          </p>
          <FocusDiv
            nav={{
              id: PROFILE_TO_WATCH_NONE_MODAL_EDIT_AVATAR_BUTTON,
              nextdown: PROFILE_TO_WATCH_NONE_MODAL_NAME_INPUT,
              parent: navMap.CONTAINER.id,
            }}
            onClick={avatarAction}
            className={styles.profileHolder}
            onFocus={handleFocus}
            onBlur={handleBlur}
          >
            {avatarDisplay}
          </FocusDiv>
          <InfoInput
            fieldNameChange={e => {
              const cloneProfile = {
                ...profile,
                age: `${age}`,
                birthdayMonth: '1',
                name: e.target.value,
              };
              dispatch(actions.setProfileFields(cloneProfile));
            }}
            fieldValue={profile?.name}
            fieldName="name"
            placeHolder="Add Name"
            inputAttributes={{
              type: 'text',
              pattern: '[a-zA-Z]+',
              required: 'required',
              maxLength: '16',
            }}
            nav={{
              id: navMap.PROFILE_TO_WATCH_NONE_MODAL_NAME_INPUT.id,
              nextdown: !(profile?.name && profile?.avatar)
                ? navMap.CANCEL_BUTTON.id
                : navMap.ACTION_BUTTON.id,
              nextup: navMap.PROFILE_TO_WATCH_NONE_MODAL_EDIT_AVATAR_BUTTON.id,
              parent: navMap.CONTAINER.id,
              keyboard: navMap.KEYBOARD,
            }}
            className={styles.infoInput}
          />
          {!(profile?.name && profile?.avatar) && (
            <p className={styles.formHint}>
              Please enter a name and choose an avatar
            </p>
          )}
        </FocusDiv>
      </BaseModal>
    </FocusModal>
  );
};

ProfileToWatchNoneModal.propTypes = {
  history: PropTypes.object,
};

export default withRouter(ProfileToWatchNoneModal);
